import functions from '../functions.js';
import { details } from './details';
import { DetailsCart } from './details-cart.js';
import { DeparturePicker } from '../components/departure-picker.js';
import { InputGuests } from '../components/input-guests';
import { Select2 } from '../components/select2';
import { FormGuide } from '../components/form-guide.js';

/**
 * Merged booking logic for details page.
 *
 * Replaces details-accommodation.js + details-package.js. Each booking block
 * lives inside its own Bootstrap nav tab pane and carries its own #booking and
 * .availability-form. The pane is routed by its data-dynamicpackage value:
 *   - 0      -> accommodation logic, initialised on page load
 *   - 1 or 2 -> package logic, initialised when its tab is shown (lazy, once)
 *
 * Each tab's logic is scoped to its pane via this.$root / this.$q(), so the two
 * tabs never touch each other's DOM. Originals kept untouched in
 * details-accommodation.js and details-package.js.
 */

$(() => {
    detailsBooking.init();
});

// Calculation loader, scoped to a tab's pane and bound to the ajax lifecycle
// (NOT the time-based details.unitsSpinner), so it stays visible until the
// calculation actually resolves and the customer sees something is happening.
function showCalcLoader($root) {
    if (!$root || !$root.length) {
        return;
    }
    if ($root.css('position') === 'static') {
        $root.css('position', 'relative');
    }
    $root.children('.calc-loader').remove();
    $root.append(
        '<div class="calc-loader" style="position:absolute;top:0;left:0;right:0;bottom:0;' +
        'display:flex;align-items:flex-start;justify-content:center;padding-top:2rem;' +
        'background:rgba(255,255,255,.65);z-index:1040;">' +
            '<div class="spinner-border text-primary" role="status">' +
                '<span class="visually-hidden">Loading...</span>' +
            '</div>' +
        '</div>'
    );
}

function hideCalcLoader($root) {
    $root && $root.children('.calc-loader').remove();
}

// scroll the customer to the booking/results area when a recalculation starts.
// Mobile: always scroll to the active tab's results (.units / .tickets).
// Desktop: bring #booking into view, but skip the jump if it's already roughly
// in place (within +/-100px of the target position).
function scrollToResults($root, selector) {
    if (functions.isMobile()) {
        const $target = $root.find(selector).first();
        $target.length && functions.scrollToElement($target, 200);
        return;
    }

    const $booking = $('#booking');
    if (!$booking.length) {
        return;
    }

    const offset = 50;
    if ($booking[0].getBoundingClientRect().top - offset <= 100) {
        return;
    }

    functions.scrollToElement($booking, offset);
}

const detailsBooking = {
    // booking block carries both data-objectgroupid and data-dynamicpackage
    bookingSelector: '#booking [data-objectgroupid][data-dynamicpackage]',
    // package tab content is loaded on demand from this endpoint/template
    dpAjaxUrl: '/services/objectDetails/',
    dpAjaxTemplate: 'dp_ajax',

    init() {
        if (!$('.page-type-details').length) {
            return;
        }

        $(this.bookingSelector).each((i, el) => {
            const $booking = $(el);
            const dynamicPackage = +$booking.attr('data-dynamicpackage');
            const $pane = $booking.closest('.tab-pane');
            const $root = $pane.length ? $pane : $booking.closest('.page-type-details');
            if (dynamicPackage === 0) {
                // accommodation: init immediately on load
                accommodationTab.init($root, $booking);
            } else if (dynamicPackage === 1 || dynamicPackage === 2) {
                // package: when its tab is shown, first load tab markup over
                // ajax, then run all package logic / bind events on it
                this.initOnTabShow($pane, () => {
                    this.loadPackageTab($root, $booking, dynamicPackage, () => {
                        // booking block may be re-rendered by the ajax markup
                        const $b = $root.find(this.bookingSelector);
                        // ajax markup misses the page-load component inits, so
                        // re-run them (guests, select2, form-guide) on the new DOM
                        this.initFormComponents($root);
                        packageTab.init($root, $b.length ? $b : $booking);
                    });
                });
            }
        });
    },

    // POST /services/details/ for the dynamic package template, inject the
    // returned markup into the pane, then run the callback (package init).
    loadPackageTab($root, $booking, dynamicPackage, cb) {
        const $target = $root.find('.dp-ajax-content').length ? $root.find('.dp-ajax-content') : $root;
        
        $target.addClass('dp-loading');

        $.post(this.dpAjaxUrl, {
            objectId: $booking.attr('data-referenceid'),
            dynamicPackage: dynamicPackage,
            template: this.dpAjaxTemplate,
        }).always(response => {
            $target.removeClass('dp-loading');

            let html = response;
            try {
                const parsed = typeof response === 'string' ? JSON.parse(response) : response;
                html = parsed && parsed.view != null ? parsed.view : response;
            } catch (e) {
                html = response;
            }

            if (html != null && html !== '') {
                $target.html(html);
            }

            cb();
        });
    },

    // re-run the form-component initialisers that globals.js normally fires on
    // page load. The package tab markup is injected later, so those components
    // (guests picker, select2 dropdowns, form guide) would otherwise be dead.
    // Guarded per-element so a re-show never double-initialises.
    initFormComponents($root) {
        $root.find('.form-guide').each((i, elem) => {
            if (!elem._formGuide) {
                elem._formGuide = new FormGuide(elem);
            }
        });

        $root.find('.input-guests').each((i, elem) => {
            if (!elem._inputGuests) {
                elem._inputGuests = new InputGuests(elem);
            }
        });

        // serviceCode is select2-ed by packageTab itself; init the rest here
        $root.find('.select2').not('[name="serviceCode"]').each((i, elem) => {
            if (!elem._select2) {
                elem._select2 = new Select2(elem);
            }
        });
    },

    initOnTabShow($pane, cb) {
        let done = false;
        const run = () => {
            if (!done) {
                done = true;
                cb();
            }
        };

        // not inside a tab, or already the active tab -> init now
        if (!$pane.length || ($pane.hasClass('active') && $pane.hasClass('show'))) {
            run();
            return;
        }

        const id = $pane.attr('id');
        const $trigger = id ? $('[data-bs-target="#' + id + '"], a[href="#' + id + '"]') : $();

        if ($trigger.length) {
            $trigger.on('shown.bs.tab', run);
        } else {
            $(document).on('shown.bs.tab', () => {
                if ($pane.hasClass('active') && $pane.hasClass('show')) {
                    run();
                }
            });
        }
    }
};

/* =========================================================================
 * ACCOMMODATION TAB (data-dynamicpackage = 0)
 * ========================================================================= */
const accommodationTab = {
    $root: null,
    $details: null,
    $booking: null,
    $form: null,
    form: null,
    $cart: null,
    cart: [],
    params: {
        objectId: null,
        unitId: null,
        guests: null,
        dates: null,
        dateFrom: null,
        dateTo: null,
        objectGroupId: null,
        calcErrorMessages: null,
    },
    defaults: {
        guests: null,
        dates: null,
        dateFrom: null,
        dateTo: null,
        objectGroupId: 1,
    },
    // scoped query helper - everything pane-local goes through here
    $q(selector) {
        return this.$root.find(selector);
    },
    init($root, $booking) {
        this.$root = $root;
        this.$details = $('.page-type-details');
        this.$booking = $booking;
        this.params.objectGroupId = this.$booking.attr('data-objectgroupid');

        if( ! (this.$details[0] && this.params.objectGroupId === "1" && +this.$booking.attr('data-dynamicpackage') === 0)) {
            return;
        }

        if(this.$q('[data-isFixDepartures]').attr('data-isFixDepartures')) {
            return;
        }

        this.$form = this.$q('.availability-form');
        this.form = this.$form[0];

        this.params.objectId = this.$booking.attr('data-objectid');
        this.params.calcErrorMessages = this.$booking.attr('data-calcErrorMessages') ? JSON.parse(this.$booking.attr('data-calcErrorMessages')) : null;
        this.allowedServices = this.$q('[data-allowedservices]').attr('data-allowedservices');
        this.allowedServices = this.allowedServices ? JSON.parse(this.allowedServices) : [];

        this.getParams();
        if(! this.params.dates && this.params.dateFrom && this.params.dateTo) {
            this.params.dates = this.params.dateFrom + ' - ' + this.params.dateTo;
        }

        if(this.form) {
            functions.setFormData(this.form, this.params);
        }

        const formData = functions.getFormData(this.$form);
        this.params.serviceCode = formData.serviceCode ? formData.serviceCode : null;

        this.getCalculation();

        this.$form.on('submit', e => {
            e.preventDefault();
            let params = functions.getFormData($(e.currentTarget));
            this.params.unitId = null;
            functions.setUrlData(params, true, true);
        });

        // check availability on form change (not only on btn click)
        this.$q('[name="dates"], [name=guests], [name=serviceCode]').on('change', () => {
            const data = functions.getFormData(this.$form);
            if ( data.dates || data.guests ) {
                this.$form.trigger('submit');
            }
        });

        this.$cart = this.$q('.details-cart');
        this.cart = this.$cart.length ? new DetailsCart(this.params) : [];

        $(window).on('hashchange', e => {
            if (this.$root && !this.$root.is(':visible')) {
                return;
            }
            this.getParams();
            //this.params.guests = this.params.adultsOnly ? this.params.guests.split(',')[0] + ',0' : this.params.guests;
            this.getCalculation(this.params);
        });

        if(this.$cart.length) {
            const $btnBooking = this.$q('.btn-booking');

            $btnBooking.on('click', e => {
                e.preventDefault();
                let cartData = [];

                accommodationTab.cart.cartItems.forEach(item => {
                    item = JSON.parse(item);
                    cartData.push({
                        'unitId': item.unitId,
                        'dateFrom': item.dateFrom,
                        'dateTo': item.dateTo,
                        'aDateFrom': item.aDateFrom,
                        'aDateTo': item.aDateTo,
                        'guests': item.guests,
                        'flightUnitId': item.flightUnitId,
                        'time': item.time,
                        'serviceCode': item.serviceCode,
                        'serviceId': item.serviceId,
                        'pricelistId': accommodationTab.params.pricelistId,
                        'dynamicPackage': 0,
                    });
                });

                if(accommodationTab.cart.cartItems.length) {
                    location = $(e.currentTarget).attr('data-href') + '?cart_data=' + JSON.stringify(cartData) + '&addAllOptionals=true&dynamicPackage=0';
                } else {
                    $('#detailsCartModal').modal('show');
                    setTimeout(() => {
                        $('#detailsCartModal').modal('hide');
                    }, 2500);
                }
            });
        }
        else {
            this.$root.on('click', '.unit .btn-book-one-unit', e => {

                e.preventDefault();
                const $btnBooking = $(e.currentTarget);

                if (! accommodationTab.params.dateFrom || ! accommodationTab.params.dateTo || ! accommodationTab.params.guests) {
                    window.setAlertBooking($btnBooking);
                    return;
                }

                let cartData = {
                    'unitId': $btnBooking.attr('data-unitid'),
                    'dateFrom': accommodationTab.params.dateFrom,
                    'dateTo': accommodationTab.params.dateTo,
                    'guests': accommodationTab.params.guests,
                    'pricelistId': $btnBooking.attr('data-pricelistid'),
                    'serviceCode': $btnBooking.attr('data-servicecode') ? encodeURIComponent($btnBooking.attr('data-servicecode')) : null,
                    'serviceId': $btnBooking.attr('data-serviceid')
                };

                let extras = $btnBooking.attr('data-extra');
                if (extras) {
                    extras = JSON.parse(extras);

                    Object.keys(extras).forEach(function(key) {
                        let val = extras[key];
                        if (key === 'optionRefId') {
                            val = encodeURIComponent(val);
                        }
                        cartData[key] = val;
                    });
                }

                location = $(e.currentTarget).attr('data-href') + '?dynamicPackage=0&cart_data=' + JSON.stringify([cartData]);
            });

        }

        this.$root.on('change', '.block-totals-per-service [type="radio"]', e => {

            const $unit = $(e.currentTarget).closest('.unit');
            const $addToCart = $unit.find('.add-to-cart');
            const $btnBooking = $unit.find('.btn-book-one-unit');
            const $radioService = $unit.find('[type="radio"]:checked');
            const $totalPerService = $radioService.closest('.total-per-service');

            let total = $totalPerService.find('[data-totalperservice]').attr('data-totalperservice');
            let totalOld = $totalPerService.find('[data-oldtotalperservice]').attr('data-oldtotalperservice');
            let currency = $totalPerService.find('[data-currency]').attr('data-currency');
            let bookingStatus = $totalPerService.find('[data-bookingstatus]').attr('data-bookingstatus');
            let pricelistid = $totalPerService.find('[data-pricelistid]').attr('data-pricelistid');
            let serviceId = $radioService.val();
            let serviceKey = $radioService.attr('data-servicekey');
            let extras = $radioService.attr('data-extra');

            if ($addToCart.length) {
                $addToCart.attr('data-' + serviceKey, serviceId);
                $addToCart.attr('data-unitcarttotal', total);
                $addToCart.attr('data-unitcarttotalold', totalOld);
                $addToCart.attr('data-currencysymbol', currency);
                $addToCart.attr('data-pricelistid', pricelistid);

                $addToCart.attr('data-datefrom', accommodationTab.params.dateFrom);
                $addToCart.attr('data-dateto', accommodationTab.params.dateTo);
                $addToCart.attr('data-guests', accommodationTab.params.guests);

            } else {
                $btnBooking.attr('data-' + serviceKey, serviceId);
                $btnBooking.attr('data-pricelistid', pricelistid);
                $btnBooking.attr('data-extra', extras);

                $btnBooking.text(bookingStatus === 'Request' ? $btnBooking.attr('data-requestLabel') : $btnBooking.attr('data-bookingLabel'));
            }
        });
    },
    getParams() {
        this.params = details.getParams(this.params, this.defaults);
        this.params.guests = this.params.guests && this.params.guests.startsWith('0') ? null : this.params.guests;
        if(this.params.dates) {
            this.splitCalendarDates(this.params.dates);
        }
    },
    splitCalendarDates(dates) {
        if(dates || dates === '') {
            const aDates = dates.split(' - ');
            this.params.dateFrom = aDates[0] ? aDates[0] : null;
            this.params.dateTo = aDates[1] ? aDates[1] : null;
        }
    },
    getCalculation(params) {
        const cParams = params ? params : this.params;

        // cParams.calcPerService = 1;
        const calculation = details.getCalculation(cParams);

        if(calculation) {

            details.unitsSpinner();
            showCalcLoader(this.$root);
            scrollToResults(this.$root, '.units');

            //this.$q('.rate-container').addClass('d-none');

            calculation
                .done(response => {
                    this.calculationDone(response, cParams);
                }).fail(error => {
                console.log(error);
            }).always(() => {
                hideCalcLoader(this.$root);
            });

            return;
        } else {
            this.$q('.total-label').addClass('d-none');
            this.$q('.price-from-label').removeClass('d-none');
            this.$q('.price').attr('data-totalperservice', null);
            this.$q('.price').attr('data-currency', null);
            this.$q('.price').attr('data-bookingstatus', null);
            this.$q('.price').attr('data-pricelistid', null);
            this.$q('.add-to-cart').attr('data-dateFrom', null);
            this.$q('.btn-request').addClass('d-none');
            this.$q('.btn-book-one-unit').removeClass('d-none');

            this.$q('.price').each(function() {
                $(this).text($(this).attr('data-price'));
            });
            this.$q('.price-old').each(function() {
                $(this).text($(this).attr('data-priceOld'));
            });
        }

        this.$q('.rate-container').removeClass('d-none');
        this.$q('.block-totals-per-service').addClass('d-none');
        this.$q('.unit-error-message').addClass('d-none');

    },
    preparePriceAndBooking(unitId, $unit, unitData, params) {

        const $totalPerServiceBlock = $unit.find('.totals-per-service');
        const totalPerServiceHtml = $totalPerServiceBlock.html();

        let discountTitle;
        $totalPerServiceBlock.html('');

        const unitServices = Object.keys(unitData.services);
        const unitServiceCount = unitServices.length;

        Object.keys(unitData.services).forEach(( serviceId) => {

            const dataPerService = unitData.services[serviceId];

            const unitCalc = dataPerService.calc;
            const currencySymbol = unitCalc.currencySymbol;
            const bookingStatus = dataPerService.booking.status;
            const unitCalcItems = unitCalc.items;
            const total = unitCalc.total.toFixed(2);
            const pricelistid = unitCalc.items[0].pricelistId;

            const $totalPerServiceHtml = $(totalPerServiceHtml);

            let priceOld = details.getCalcTotalOld(unitCalcItems);
            priceOld = +priceOld.toFixed(2);

            let serviceText = '';
            if (dataPerService.customServiceName) {
                serviceText = dataPerService.customServiceName;
            } else {
                accommodationTab.allowedServices.forEach((allowedService) => {
                    if (dataPerService.serviceId && +allowedService.id === +dataPerService.serviceId ||
                        dataPerService.serviceCode && allowedService.key === dataPerService.serviceCode){
                        serviceText = allowedService.text;
                    }
                });

                if ( ! serviceText)
                {
                    if (dataPerService.serviceName) {
                        serviceText = dataPerService.serviceName;
                    }
                }
            }

            let totalRounded = functions.formatMoney(total, 0, ',', '.');
            $totalPerServiceHtml.attr('style', 'order:' + totalRounded);
            $totalPerServiceHtml.addClass('order-' + totalRounded);
            $totalPerServiceHtml.attr('data-order' , totalRounded);

            const $radio = $totalPerServiceHtml.find('[type="radio"]');

            const radioVal = dataPerService.serviceId || dataPerService.serviceCode;
            $radio.val(radioVal);
            $radio.attr('name', 'service-' + unitId);
            $radio.attr('id', 'service-' + unitId + '-' + radioVal);
            $radio.attr('data-servicekey', dataPerService.serviceCode ? 'serviceCode' : 'serviceId');

            let extra = {};
            if (dataPerService.optionRefId) {
                extra.optionRefId = dataPerService.optionRefId;
            }

            $radio.attr('data-extra', JSON.stringify(extra));

            const $label = unitServiceCount > 1 ? $totalPerServiceHtml.find('label') : $unit.find('.service-label');
            $label.text(serviceText);
            $label.attr('for', 'service-' + unitId + '-' + radioVal);

            const $oldTotal = unitServiceCount > 1 ? $totalPerServiceHtml.find('[data-oldtotalperservice]') : $unit.find('.price-old');
            $oldTotal.text(priceOld > total ? currencySymbol + ' ' + functions.formatMoney(priceOld, 0, ',', '.') : '');
            $oldTotal.attr('data-oldtotalperservice', priceOld > total ? priceOld : '');

            const $total = unitServiceCount > 1 ? $totalPerServiceHtml.find('[data-totalperservice]') : $unit.find('.price');
            $total.text(currencySymbol + ' ' + functions.formatMoney(total, 0, ',', '.'));
            $total.attr('data-totalperservice', total);
            $total.attr('data-currency', currencySymbol);
            $total.attr('data-bookingStatus', bookingStatus);
            $total.attr('data-pricelistid', pricelistid);

            $totalPerServiceBlock.append($totalPerServiceHtml[0].outerHTML);
        });

        let minp = null;
        $totalPerServiceBlock.find("[data-order]").each((index, elem) => {
            let p = +$(elem).attr('data-order');
            if (minp === null || p < minp) {
                minp = p;
            }
        });

        $totalPerServiceBlock.find(".order-" + minp).find('[type="radio"]').click();
        $totalPerServiceBlock.find(".order-" + minp).addClass('border-0');
    },
    calculationDone(response, params) {
        if(response.status) {
            const data = response.data || [];

            Object.entries(data).forEach(unit => {
                const unitId = unit[0];
                let unitData = unit[1];
                const $unit = this.$q('.unit[data-unitid=' + unitId + ']');
                if ( ! $unit.length ) return;
                let isBookable = false;

                // normalize response: wrap flat data into services structure
                if(!unitData.services && unitData.calc) {
                    unitData = {
                        services: {
                            0: {
                                calc: unitData.calc,
                                booking: unitData.booking,
                                status: unitData.status,
                                message: unitData.message,
                                condition: unitData.condition || '',
                                isAvailable: unitData.isAvailable,
                                serviceId: null,
                                serviceCode: null
                            }
                        }
                    };
                }

                const unitServiceCount = unitData.services ? Object.values(unitData.services).length : 0;
                this.$q('.rate-container').length && this.$q('.rate-container').toggleClass('d-none', unitServiceCount > 1);

                if(unitData.services && details.isBookablePerService(unitData.services)) {
                    isBookable = details.isBookablePerService(unitData.services);
                    const total = details.getMinPricePerService(unitData.services);
                    accommodationTab.preparePriceAndBooking(unitId, $unit, unitData, params);
                    $unit.prop('style', 'order: ' + parseInt(total));
                } else {
                    $unit.prop('style', 'order: 100000;');
                }

                let error = ! isBookable ? details.getCalcErrorMessage(this.params.calcErrorMessages, unitData) : null;

                $unit.find('.unit-error-message').toggleClass('d-none', isBookable);
                $unit.find('.unit-error-message').text(error);

                if (isBookable) {
                    $unit.find('.price-from-label').addClass('d-none');
                    $unit.find('.total-label').removeClass('d-none');
                } else {
                    $unit.find('.total-label').addClass('d-none');
                    $unit.find('.price-from-label').removeClass('d-none');
                }

                $unit.find('.block-totals-per-service').toggleClass('d-none', ! isBookable || unitServiceCount < 2);
                $unit.find('.fast-and-secure-label').toggleClass('d-none', ! isBookable);
                $unit.find('.add-to-cart').toggleClass('d-none', ! isBookable);
                $unit.find('.btn-book-one-unit').toggleClass('d-none', ! isBookable);

                // set button text based on booking status
                if(isBookable && unitData.services) {
                    let hasRequest = false;
                    Object.values(unitData.services).forEach(service => {
                        if(service.booking && service.booking.status === 'Request') {
                            hasRequest = true;
                        }
                    });
                    const btnText = hasRequest ? 'Pošaljite upit' : 'Rezervišite';
                    $unit.find('.btn-book-one-unit').text(btnText);
                }

                $unit.find('.btn-request').toggleClass('d-none', isBookable);

            });
        }
    }
};

/* =========================================================================
 * PACKAGE TAB (data-dynamicpackage = 1 or 2)
 * ========================================================================= */
const packageTab = {
    $root: null,
    $details: null,
    $booking: null,
    $form: null,
    form: null,
    $cart: null,
    cart: [],
    params: {
        objectId: null,
        unitId: null,
        guests: null,
        dates: null,
        dateFrom: null,
        dateTo: null,
        aDateFrom: null,
        aDateTo: null,
        objectGroupId: null,
        calcErrorMessages: null,
        dynamicPackage: null,
        time: null,
    },
    defaults: {
        guests: null,
        dates: null,
        dateFrom: null,
        dateTo: null,
        objectGroupId: 1,
    },
    // scoped query helper - everything pane-local goes through here
    $q(selector) {
        return this.$root.find(selector);
    },
    init($root, $booking) {
        this.$root = $root;
        this.$details = $('.page-type-details');
        this.$booking = $booking;
        this.params.objectGroupId = this.$booking.attr('data-objectgroupid');
        this.params.dynamicPackage = +this.$booking.attr('data-dynamicpackage');

        if( ! (this.$details[0] && this.params.objectGroupId === "1" && [1,2].includes(this.params.dynamicPackage))) {
            return;
        }

        this.$form = this.$q('.availability-form');
        this.form = this.$form[0];

        this.params.objectId = this.$booking.attr('data-objectid');
        this.params.referenceId = this.$booking.attr('data-referenceid');
        this.params.calcErrorMessages = this.$booking.attr('data-calcErrorMessages') ? JSON.parse(this.$booking.attr('data-calcErrorMessages')) : null;
        this.allowedServices = this.$q('[data-allowedservices]').attr('data-allowedservices');
        this.allowedServices = this.allowedServices ? JSON.parse(this.allowedServices) : [];

        this.getParams();
        if(! this.params.dates && this.params.dateFrom && this.params.dateTo) {
            this.params.dates = this.params.dateFrom + ' - ' + this.params.dateTo;
        }

        //this.getCalculation();

        this.$form.on('submit', e => {
            e.preventDefault();
            let params = functions.getFormData($(e.currentTarget));
            //this.params.unitId = null;
            functions.setUrlData(params, true, true);
        });

        // check availability on form change (not only on btn click)
        /*this.$q('[name="dates"], [name="guests"]').on('change', () => {
            const data = functions.getFormData(this.$form);
            if (data.dates && data.guests) {
                this.$form.trigger('submit');
            }
        });*/

        this.prepareAvailabilityForm();

        this.$cart = this.$q('.details-cart');
        this.cart = this.$cart.length ? new DetailsCart(this.params) : [];

        $(window).on('hashchange', e => {
            if (this.$root && !this.$root.is(':visible')) {
                return;
            }
            this.getParams();
            //this.params.guests = this.params.adultsOnly ? this.params.guests.split(',')[0] + ',0' : this.params.guests;
            this.showTransportTicket();

            this.getCalculation(this.params);
        });

        if(this.$cart.length) {
            const $btnBooking = this.$q('.btn-booking');

            $btnBooking.on('click', e => {
                e.preventDefault();
                let cartData = [];

                packageTab.cart.cartItems.forEach(item => {
                    item = JSON.parse(item);
                    cartData.push({
                        'unitId': item.unitId,
                        'dateFrom': item.dateFrom,
                        'dateTo': item.dateTo,
                        'aDateFrom': item.aDateFrom,
                        'aDateTo': item.aDateTo,
                        'guests': item.guests,
                        'flightUnitId': item.transportUnitId,
                        'placeFromId': item.placeFromId,
                        'time': item.time,
                        'serviceCode': item.serviceCode,
                        'serviceId': item.serviceId,
                        'pricelistId': packageTab.params.pricelistId,
                        'dynamicPackage': packageTab.params.dynamicPackage
                    });
                });

                if(packageTab.cart.cartItems.length) {
                    location = $(e.currentTarget).attr('data-href') + '?cart_data=' + JSON.stringify(cartData) + '&addAllOptionals=true&dynamicPackage=' + packageTab.params.dynamicPackage;
                } else {
                    $('#detailsCartModal').modal('show');
                    setTimeout(() => {
                        $('#detailsCartModal').modal('hide');
                    }, 2500);
                }
            });
        }
        else {
            this.$root.on('click', '.unit .btn-book-one-unit', e => {

                e.preventDefault();
                const $btnBooking = $(e.currentTarget);

                if (! packageTab.params.dateFrom || ! packageTab.params.dateTo || ! packageTab.params.guests) {
                    window.setAlertBooking($btnBooking);
                    return;
                }

                let cartData = {
                    'unitId': $btnBooking.attr('data-unitid'),
                    'dateFrom': packageTab.params.dateFrom,
                    'dateTo': packageTab.params.dateTo,
                    'aDateFrom': packageTab.params.aDateFrom,
                    'aDateTo': packageTab.params.aDateTo,
                    'time': packageTab.params.time,
                    'guests': packageTab.params.guests,
                    'flightUnitId': packageTab.params.transportUnitId,
                    'placeFromId': packageTab.params.placeFromId,
                    'pricelistId': $btnBooking.attr('data-pricelistid'),
                    'serviceCode': $btnBooking.attr('data-servicecode') ? encodeURIComponent($btnBooking.attr('data-servicecode')) : null,
                    'serviceId': $btnBooking.attr('data-serviceid'),
                    'dynamicPackage': packageTab.params.dynamicPackage
                };

                let extras = $btnBooking.attr('data-extra');
                if (extras) {
                    extras = JSON.parse(extras);

                    Object.keys(extras).forEach(function(key) {
                        let val = extras[key];
                        if (key === 'optionRefId') {
                            val = encodeURIComponent(val);
                        }
                        cartData[key] = val;
                    });
                }

                location = $(e.currentTarget).attr('data-href') + '?dynamicPackage='+packageTab.params.dynamicPackage+'&cart_data=' + JSON.stringify([cartData]);
            });

        }

        this.$root.on('change', '.block-totals-per-service [type="radio"]', e => {

            const $unit = $(e.currentTarget).closest('.unit');
            const $addToCart = $unit.find('.add-to-cart');
            const $btnBooking = $unit.find('.btn-book-one-unit');
            const $radioService = $unit.find('[type="radio"]:checked');
            const $totalPerService = $radioService.closest('.total-per-service');

            let total = $totalPerService.find('[data-totalperservice]').attr('data-totalperservice');
            let totalOld = $totalPerService.find('[data-oldtotalperservice]').attr('data-oldtotalperservice');
            let currency = $totalPerService.find('[data-currency]').attr('data-currency');
            let bookingStatus = $totalPerService.find('[data-bookingstatus]').attr('data-bookingstatus');
            let pricelistid = $totalPerService.find('[data-pricelistid]').attr('data-pricelistid');
            let serviceId = $radioService.val();
            let serviceKey = $radioService.attr('data-servicekey');
            let extras = $radioService.attr('data-extra');

            if ($addToCart.length) {
                $addToCart.attr('data-' + serviceKey, serviceId);
                $addToCart.attr('data-unitcarttotal', total);
                $addToCart.attr('data-unitcarttotalold', totalOld);
                $addToCart.attr('data-currencysymbol', currency);

                $addToCart.attr('data-datefrom', packageTab.params.dateFrom);
                $addToCart.attr('data-dateto', packageTab.params.dateTo);
                $addToCart.attr('data-guests', packageTab.params.guests);

                $addToCart.attr('data-adatefrom', packageTab.params.aDateFrom);
                $addToCart.attr('data-adateto', packageTab.params.aDateTo);

                $addToCart.attr('data-transportunitid', packageTab.params.transportUnitId);
                $addToCart.attr('data-placefromid', packageTab.params.placeFromId);
                $addToCart.attr('data-time', packageTab.params.time);

            } else {
                $btnBooking.attr('data-' + serviceKey, serviceId);
                $btnBooking.attr('data-pricelistid', pricelistid);
                $btnBooking.attr('data-extra', extras);

                $btnBooking.text(bookingStatus === 'Request' ? $btnBooking.attr('data-requestLabel') : $btnBooking.attr('data-bookingLabel'));
            }
        });
    },
    prepareAvailabilityForm() {
        this.$q('.tickets').append('<div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>');
        $.post('/services/dynamic_package_availability_form_data/', {
            objectId: this.params.referenceId,
            dynamicPackage: this.params.dynamicPackage,
            language: functions.getLang(),
            flightAvailabilityStatus: true,
            placeFromId: this.params.placeFromId,
        }).always(response => {
            response = response ? JSON.parse(response) : null;

            if (response.status) {
                const prepared = this.getPackageDepartures(response.data.items, response.data.flights);
                this.departurePoints = response.data.departurePoints
                    ? response.data.departurePoints.filter((point, index, array) =>
                        array.findIndex(p => p.placeId === point.placeId) === index)
                    : response.data.departurePoints;
                const serviceCodes = response.data.serviceCodes;
                //const $nights = this.$q("[name='nights']");
                this.enable = prepared.enable;
                this.flights = prepared.flights;
                this.prices = prepared.prices;

                const $service = this.$q("[name='serviceCode']");
                const $serviceWrapper = $service.parents('.service-code-wrapper');
                if (serviceCodes.length) {
                    $serviceWrapper.removeClass('d-none')
                    serviceCodes.forEach((serviceCode, key) => {
                        this.allowedServices.forEach((allowedService) => {
                            if (allowedService.key === serviceCode) {
                                serviceCodes[key] = {id: allowedService.key};
                                serviceCodes[key].text = allowedService.text;

                            }
                        });
                    });

                    $service.select2({
                        data: serviceCodes,
                        minimumResultsForSearch: -1
                    });
                } else {
                    $serviceWrapper.addClass('d-none');
                }

                let nights = this.enable.map(i => i['nights']);
                nights = nights.filter((value, index, array) => array.indexOf(value) === index).sort((a, b) => {
                    return a - b
                });

                if (nights.length) {
                    this.$q('.package-duration-wrapper').removeClass('d-none');
                    this.$q('.package-duration').text(nights.join(', ') + ' noćenja');

                    let _nights = [];
                    nights.forEach((night, key) => {
                        _nights.push({id: night, text: night})

                        if (!this.params.nights) {
                            this.params.nights = night;
                        }
                    });

                    /*$nights.select2({
                        data: _nights,
                        minimumResultsForSearch: -1
                    });*/

                    //setTimeout(() => $nights.val(this.params.nights).trigger('change'));

                } else {
                    this.$q('.package-duration-wrapper').addClass('d-none');
                }

                const $detailsDatepicker = this.$q("#details-datepicker");
                $detailsDatepicker.attr('data-departures', JSON.stringify(this.enable));
                $detailsDatepicker.attr('data-flights', JSON.stringify(this.flights));
                $detailsDatepicker.attr('data-prices', JSON.stringify(this.prices));
                $detailsDatepicker.attr('data-departurePoints', JSON.stringify(this.departurePoints));

                $detailsDatepicker.find('[data-input]').each((i, elem) => {
                    elem._departurePicker = new DeparturePicker(elem);
                });

                this.setDefaultDeparture();
                functions.setFormData(this.form, this.params);

                // showFlightTickets must MANDATORY be after setFormData
                this.showTransportTicket();
                //this.params.dynamicPackage == 2 && this.setDeparturePoints();
                this.setDeparturePoints();

                this.getCalculation();

                this.$form.on('change', e => {
                    e.preventDefault();
                    const $target = $(e.currentTarget);
                    const data = functions.getFormData($target);

                    this.params.dateFrom = data.dateFrom;
                    this.params.dateTo = data.dateTo;
                    this.params.guests = data.guests;
                    this.params.time = data.time;
                    this.params.duration = data.duration;
                    this.params.transportUnitId = data.transportUnitId;
                    this.params.placeFromId = data.placeFromId;

                    functions.setUrlData(
                        {
                            'dateFrom': this.params.dateFrom,
                            'dateTo': this.params.dateTo,
                            'aDateFrom': this.params.aDateFrom,
                            'aDateTo': this.params.aDateTo,
                            'guests': this.params.guests,
                            'time': this.params.time,
                            'duration': this.params.duration,
                            'transportUnitId': this.params.transportUnitId,
                            'placeFromId': this.params.placeFromId,
                        },
                        true,
                        true);
                });

                this.$root.on('click', '.ticket', e => {
                    this.setTransportTicket($(e.currentTarget), true);
                });

            }
        });
    },
    getPackageDepartures(rates, flightTitles) {
        let departures = {};

        Object.keys(rates).forEach(index => {
            let rate = rates[index];
            let flight = rate.flight;
            // let unique = rate.dateFrom + ',' + rate.dateTo + ',' + flight.timeDepartureFrom + ',' + flight.timeReturnFrom;
            //let unique = rate.aDateFrom + ',' + rate.aDateTo + ',' + rate.dateFrom + ',' + rate.dateTo;
            let unique = rate.dateFrom + ',' + rate.dateTo;

            let aDateFrom = rate.aDateFrom;
            let aDateTo = rate.aDateTo;

            let flightUnitId = flight.unitId;

            if ( flight.timeDepartureFrom ) {
                flight.time = flight.timeDepartureFrom + ',' + (flight.timeReturnFrom || '');
            }

            let flightUnique = flightUnitId + (flight.time ? '_' + flight.time : '');

            if ( ! departures[unique]) {
                let flights = {};
                flights[flightUnique] = flight;

                let rates = {};
                rates[rate.id] = rate;

                departures[unique] = {
                    'dateFrom'      : rate.dateFrom,
                    'dateTo'        : rate.dateTo,
                    'aDateFrom'     : aDateFrom,
                    'aDateTo'       : aDateTo,
                    'time'          : flight.time || '',
                    'duration'      : rate.accommodationDuration,
                    'nights'        : rate.accommodationDuration,
                    'flightUnitId'  : flightUnitId,
                    'flights'       : flights,
                    'rates'         : rates,
                };
            } else {
                let departure = departures[unique];
                departure.flights[flightUnique] = flight;
                departure.rates[rate.id] = rate;
            }
        });

        this.departures = departures;

        let enable = [];
        let prices = {};
        let flights = [];

        Object.keys(departures).forEach(index => {
            let rate = departures[index];

            enable.push({
                dateFrom: rate['dateFrom'],
                dateTo: rate['dateTo'],
                aDateFrom: rate['aDateFrom'],
                aDateTo: rate['aDateTo'],
                duration: rate['duration'],
                nights: rate['duration'],
                time: rate['time'],
                flightUnitId: rate['flightUnitId'],
            });

            let arrival = rate.dateFrom;
            let departure = rate.dateTo;

            prices[index] = packageTab.getDeparturePrices(rate);
            let departureDay = packageTab.getWeekDay(arrival);
            let returnArrivalDay = packageTab.getWeekDay(departure);

            Object.keys(rate.flights).forEach(function(index) {

                let flight = rate.flights[index];

                if ( flight.timeDepartureFrom ) {
                    flight.time = flight.timeDepartureFrom + ',' + (flight.timeReturnFrom || '');
                }

                let timeDepartureFrom = flight['timeDepartureFrom'];
                let timeDepartureTo = flight['timeDepartureTo'];
                let timeReturnFrom = flight['timeReturnFrom'];
                let timeReturnTo = flight['timeReturnTo'];
                let placeFromId = flight['placeFromId'];
                let placeFromName = flight['placeFrom'];
                let placeToName = flight['placeTo'];
                let departureDuration = flight['departureDuration'];
                let returnDuration = flight['returnDuration'];
                let arrivalDate = flight['arrivalDate'];
                let returnDate = flight['returnDate'];
                let availabilityStatus = flight['availabilityStatus'];

                let flightTitle;
                let flightSmallImg;
                let supplierTitle;
                flightTitles.forEach((ft) => {
                    if (ft.id == flight.objectId) {
                        flightTitle = ft.title ? ft.title : ft.supplierTitle;
                        flightSmallImg = ft.images.small ? ft.images.small : '';
                        supplierTitle = ft.supplierTitle;
                    }
                });

                let arrivalDay = packageTab.getWeekDay(arrivalDate);
                let returnDepartureDay = packageTab.getWeekDay(returnDate);

                flights.push({
                    duration              : rate.duration,
                    dateFrom              : rate.dateFrom,
                    dateTo                : rate.dateTo,
                    aDateFrom             : rate.aDateFrom,
                    aDateTo               : rate.aDateTo,
                    flightId              : flight.objectId,
                    flightTitle           : flightTitle,
                    supplierTitle         : supplierTitle,
                    flightSmallImg        : flightSmallImg,
                    flightUnitId          : flight.unitId,
                    departureDay          : departureDay,
                    returnDepartureDay    : returnDepartureDay,
                    arrivalDay            : arrivalDay,
                    returnArrivalDay      : returnArrivalDay,
                    timeDepartureFrom     : timeDepartureFrom,
                    timeDepartureTo       : timeDepartureTo,
                    timeReturnFrom        : timeReturnFrom,
                    timeReturnTo          : timeReturnTo,
                    time                  : flight.time,
                    placeFromId           : placeFromId,
                    placeFromName         : placeFromName,
                    placeToName           : placeToName,
                    departureDuration     : departureDuration,
                    returnDuration        : returnDuration,
                    arrivalDate           : arrivalDate,
                    returnDate            : returnDate,
                    availabilityStatus    : availabilityStatus,
                });
            });

        });

        return {
            enable : enable,
            prices : prices,
            flights : flights
        };

    },
    getWeekDay(date) {

        const d = new Date(date);
        const days = [
            "nedelja",
            "ponedeljak",
            "utorak",
            "sreda",
            "četvrtak",
            "petak",
            "subota"
        ];

        let day = d.getDay()

        return days[day];
    },
    getDeparturePrices(rate) {
        let departurePrices = {};
        let rates = rate.rates;

        if (!rates || Object.keys(rates).length === 0) {
            // console.warn('getDeparturePrices - rates empty or missing');
            return departurePrices;
        }

        Object.keys(rates).forEach(function(rateId) {
            let unitRate = rates[rateId];

            if (!unitRate || !unitRate.unitId || !unitRate.unitId.id) {
                return;
            }

            let unitId = unitRate.unitId.id;
            let bestPrice = unitRate.priceWithDiscount ? parseFloat(unitRate.priceWithDiscount) : parseFloat(unitRate.price);

            // keep only lowest price per unit
            if (!departurePrices[unitId] || bestPrice < parseFloat(departurePrices[unitId].bestPrice)) {
                departurePrices[unitId] = {
                    bestPrice: bestPrice.toString(),
                    price: '€ ' + functions.formatMoney(unitRate.price, 0, '', ''),
                    priceWithDiscount: unitRate.priceWithDiscount ? ('€ ' + functions.formatMoney(unitRate.priceWithDiscount, 0, '', '')) : null,
                    paymentKey: unitRate.paymentTypeKey == 'Once' ? 'po boravku' : 'po osobi'
                };
            }
        });

        // console.log('getDeparturePrices result:', departurePrices);
        return departurePrices;
    },
    getDeparturePoints (params) {
        return $.post('/services/departure_points/', { dynamicPackage: params.dynamicPackage, objectId: params.objectId });
    },
    setDeparturePoints () {
        const $placeFromId = this.$q('[name="placeFromId"]');
        $placeFromId.empty();

        this.departurePoints && this.departurePoints.forEach(departurePoints => {
            const newOption = new Option(departurePoints.placeName, departurePoints.placeId, false, this.params.placeFromId == departurePoints.placeId);
            $placeFromId.append(newOption);
        });

        /*this.getDeparturePoints({
            dynamicPackage: this.params.dynamicPackage,
            objectId: this.params.referenceId
        }).then(response => {
            departurePoints = response;

            departurePoints.forEach(departurePoints => {
                const newOption = new Option(departurePoints.name, departurePoints.id, false, this.params.placeFromId == departurePoints.id);
                $placeFromId.append(newOption);
            });
        });*/

        //$placeFromId.val(this.params.placeFromId).trigger('change');
    },
    showPlacesFrom (tickets) {
        // tickets are now collapsers: keep them all visible (collapsed), don't
        // hide the non-active ones
        //this.$q('.tickets').addClass('show-active');
        //const $transportUnitId = this.$q('[name="transportUnitId"]');
        //$transportUnitId.empty();

        /*tickets.forEach(ticket => {
            const newOption = new Option(ticket.placeFromName, ticket.flightUnitId, false, false);
            $transportUnitId.append(newOption);
        });*/

        //$transportUnitId.val(this.params.transportUnitId);
    },
    showTransportTicket(){
        const dataTransport = JSON.parse(this.$q('[data-flights]').attr('data-flights'));
        let dateFrom = this.params.dateFrom;
        let dateTo = this.params.dateTo;

        let duration = this.params.duration;
        let time = this.params.time != ' ' && (this.params.time && ! this.params.time.startsWith('null')) ? this.params.time : null;
        time = time == 'undefined' ? null : time;

        if (duration) {
            if (! this.$q('.duration-item[data-datefrom="' + dateFrom + '"][data-dateto="' + dateTo + '"][data-duration="' + duration + '"]').length) {
                duration = this.params.duration = null;
            }
        }

        if (! duration && this.$q('.duration-item.active').length) {
            duration && this.$q('.duration-item.active').attr('data-duration');
        }

        let tickets = dataTransport.filter(ticket => {
            return ticket.dateFrom == dateFrom
                && ticket.dateTo == dateTo
                && (duration ? ticket.duration == duration : true)
                //&& (time && ticket.time ? ticket.time == time : true);
        });

        tickets.sort((a, b) => {
            return a.duration - b.duration;
        });

        //this.params.dynamicPackage == 2 && this.showPlacesFrom(tickets);
        this.showPlacesFrom(tickets);

        this.renderTransportTickets(tickets);
    },
    renderTransportTickets(tickets) {
        const $tickets = this.$q('.tickets').empty();

        // accordion parent so only one ticket stays expanded at a time
        const accordionId = $tickets.attr('id') || ('tickets-accordion-' + (this.$root.attr('id') || 'dp'));
        $tickets.attr('id', accordionId);

        const isFlight = this.params.dynamicPackage == 1;

        let hasActiveTicket = tickets.find((ticket) => {
            return ticket.flightUnitId == this.params.transportUnitId;
        });

        tickets.forEach((ticket, index) => {
            // times can differ per departure point - use the matching point's item for this ticket's dates, if found
            const departurePoint = (this.departurePoints || []).find(dp => dp.placeId == this.params.placeFromId);
            const dpItem = departurePoint && departurePoint.items
                ? departurePoint.items.find(item => item.arrivalDate <= ticket.dateFrom && ticket.dateTo <= item.returnDate)
                : null;

            if (dpItem) {
                ticket.timeDepartureFrom = dpItem.timeDepartureFrom;
                // ticket.timeDepartureTo = dpItem.timeDepartureTo;
                // ticket.timeReturnFrom = dpItem.timeReturnFrom;
                ticket.timeReturnTo = dpItem.timeReturnTo;
            }

            const time = ticket.timeDepartureFrom && ticket.timeReturnFrom ? ticket.timeDepartureFrom + ',' + ticket.timeReturnFrom : null;
            this.params.time = ! this.params.time && index == 0 ? time : this.params.time;

            if (ticket.flightTitle == 'Arena Travel d.o.o.') {
                ticket.flightTitle = null;
            }

            if ( ! hasActiveTicket && index == 0) {
                this.params.transportUnitId = ticket.flightUnitId;
            }

            if (! ticket.flightSmallImg) {
                ticket.flightSmallImg = isFlight
                ? '/wp-content/uploads/avio-aranzmani.jpg'
                : '/wp-content/uploads/bus-aranzmani.jpg';
            }

            const collapseId = accordionId + '-c' + index;

            $tickets.append(`
                <div class="ticket ${ isFlight ? 'ticket-flight' : 'ticket-bus'}"
                    data-aDateFrom="${ ticket.aDateFrom }"
                    data-aDateTo="${ ticket.aDateTo }"
                    data-transportUnitId="${ ticket.flightUnitId }"
                    data-time="${ ticket.time }"
                    data-placeFromId="${ ticket.placeFromId }">

                    <div class="ticket-tail">
                        <div class="ticket-tail-check">
                            <i class="fa-light fa-check"></i>
                            <div class="label-selected"
                                data-labelSelected="Odabrano"
                                data-labelSelect="Odaberi">
                                Odaberi
                            </div>
                        </div>
                    </div>

                    <div class="ticket-summary collapsed row align-items-center gap-1 gx-0"
                        role="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#${ collapseId }"
                        aria-expanded="false"
                        aria-controls="${ collapseId }">
                        <div class="ticket-supplier fw-bold col-12">${ ticket.supplierTitle || ticket.flightTitle || '' }</div>
                        <div class="ticket-summary-route col-12 ">
                            <div class="row align-items-center justify-content-between">
                                <div class="ticket-placeFrom col-5">${ ticket.placeFromName }</div>
                                <i class="fa-solid ${ isFlight ? 'fa-plane' : 'fa-bus-side' } col"></i>
                                <div class="ticket-placeTo col-5 text-end">${ ticket.placeToName }</div>
                            </div>
                        </div>
                        <span class="ticket-toggle btn btn-sm btn-outline-secondary ms-auto">
                            Detalji o karti
                            <i class="ticket-chevron fa-light fa-chevron-down ms-1"></i>
                        </span>
                    </div>

                    <div id="${ collapseId }" class="collapse ticket-body" data-bs-parent="#${ accordionId }">

                    <div class="row g-0">
                        <div class="col-lg">
                            <div class="ticket-departure">
                                <div class="row">
                                    <div class="col-12 col-xl-auto">
                                        <div class="ticket-placeFrom"
                                            data-bs-toggle="tooltip"
                                            data-bs-placement="top"
                                            title="${ ticket.placeFromName }">
                                            ${ ticket.placeFromName }
                                        </div>
                                        <div class="fw-bold">${ functions.formatDate(ticket.dateFrom) }</div>
                                        <div class="small">${ ticket.departureDay }${ ticket.timeDepartureFrom ? ' ' + ticket.timeDepartureFrom + ' h' : '' }</div>
                                    </div>
                                    <div class="col d-flex justify-content-between align-items-center">
                                        <hr />
                                        <div class="d-flex flex-column align-items-center text-center${ ticket.departureDuration ? '  mt-3 mt-lg-4 mb-3 mb-lg-0' : '' }">
                                            <i class="fa-solid ${ isFlight ? 'fa-plane' : 'fa-bus-side' }"></i>
                                            ${ ticket.departureDuration ? `<span class="small"><small>${ ticket.departureDuration } h</small></span>` : '' }
                                        </div>
                                        <hr />
                                    </div>
                                    <div class="col-12 col-xl-auto">
                                        <div class="ticket-placeTo"
                                            data-bs-toggle="tooltip"
                                            data-bs-placement="top"
                                            title="${ ticket.placeToName }">
                                            ${ ticket.placeToName }
                                        </div>
                                        <div class="fw-bold">${ functions.formatDate(ticket.arrivalDate) }</div>
                                        <div class="small">${ ticket.arrivalDay }${ ticket.timeDepartureTo ? ' ' + ticket.timeDepartureTo + ' h' : '' }</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-lg">
                            <div class="ticket-return">
                                <div class="row">
                                    <div class="col-12 col-xl-auto">
                                        <div class="ticket-placeTo"
                                            data-bs-toggle="tooltip"
                                            data-bs-placement="top"
                                            title="${ ticket.placeToName }">
                                            ${ ticket.placeToName }
                                        </div>
                                        <div class="fw-bold">${ functions.formatDate(ticket.returnDate) }</div>
                                        <div class="small">${ ticket.returnDepartureDay }${ ticket.timeReturnFrom ? ' ' + ticket.timeReturnFrom + ' h' : '' }</div>
                                    </div>
                                    <div class="col d-flex justify-content-between align-items-center">
                                        <hr />
                                        <div class="d-flex flex-column align-items-center text-center${ ticket.returnDuration ? ' mt-3 mt-lg-4 mb-3 mb-lg-0' : '' }">
                                            <i class="fa-solid ${ isFlight ? 'fa-plane' : 'fa-bus-side' }"></i>
                                            ${ ticket.returnDuration ? `<span class="small"><small>${ ticket.returnDuration } h</small></span>` : '' }
                                        </div>
                                        <hr />
                                    </div>
                                    <div class="col-12 col-xl-auto">
                                        <div class="ticket-placeFrom"
                                            data-bs-toggle="tooltip"
                                            data-bs-placement="top"
                                            title="${ ticket.placeFromName }">
                                            ${ ticket.placeFromName }
                                            </div>
                                        <div class="fw-bold">${ functions.formatDate(ticket.dateTo) }</div>
                                        <div class="small">${ ticket.returnArrivalDay }${ ticket.timeReturnTo ? ' ' + ticket.timeReturnTo + ' h' : '' }</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    

                    </div>

                </div>
            `);

            //this.params.aDateFrom = this.$q('.ticket.active').attr('data-adatefrom');
            //this.params.aDateTo = this.$q('.ticket.active').attr('data-adateto');
        });

        let $ticket = this.$q('.ticket[data-transportUnitId="' +  this.params.transportUnitId + '"]').first();

        if (this.params.placeFromId) {
            const $placeTicket = this.$q('.ticket[data-placefromid="' +  this.params.placeFromId + '"]');
            if ($placeTicket.length) $ticket = $placeTicket;
        }

        //const $ticket = this.$q('.ticket[data-transportUnitId="' +  this.params.transportUnitId + '"]');//.first();
        // select the active ticket but keep every ticket collapsed until the
        // user opens one
        $ticket.length && this.setTransportTicket($ticket);
    },
    setTransportTicket($ticket, triggerChange = false) {
        this.$q('.ticket').removeClass('active');

        const $prevLabelSelected = this.$q('.ticket').find('.label-selected');
        $prevLabelSelected.text($prevLabelSelected.attr('data-labelSelect'));

        $ticket.addClass('active');
        const $labelSelected = $ticket.find('.label-selected');
        $labelSelected.text($labelSelected.attr('data-labelSelected'));

        this.getParams();
        let time = $ticket.attr('data-time');
        let aDateFrom = $ticket.attr('data-adatefrom');
        let aDateTo = $ticket.attr('data-adateto');
        let unitId = $ticket.attr('data-transportunitid');

        const $placeFromId = this.$q('[name="placeFromId"]');

        setTimeout(() => {
            if ($ticket.hasClass('active') && $placeFromId.length && $placeFromId.val()) {
                const placeFrom = $placeFromId.parents('.form-floating').find('.select2-selection__rendered').text();
                $ticket.find('.ticket-placeFrom').text(placeFrom);
            }
        });

        //this.$q('[name="time"]').val(time);
        //this.$q('[name="aDateFrom"]').val(aDateFrom);
        //this.$q('[name="aDateTo"]').val(aDateTo);

        this.params.time = time;
        this.params.aDateFrom = aDateFrom;
        this.params.aDateTo = aDateTo;
        this.params.transportUnitId = unitId;

        triggerChange && this.$form.change();
    },
    setDefaultDeparture() {
        this.params.dateFrom = ! this.params.dateFrom && this.departures ? this.departures[Object.keys(this.departures)[0]].dateFrom : this.params.dateFrom;
        this.params.dateTo = ! this.params.dateTo && this.params.dateFrom && this.departures ? this.departures[Object.keys(this.departures)[0]].dateTo : this.params.dateTo;

        let departure = this.departures ? this.departures[this.params.dateFrom + ',' + this.params.dateTo] : null;
        ! this.params.time && departure && (this.params.time = departure.time);

        departure = departure || this.departures[this.params.dateFrom + ',' + this.params.dateTo + ',' + this.params.time]
        this.params.aDateFrom = departure && departure.aDateFrom ? departure.aDateFrom : null;
        this.params.aDateTo = departure && departure.aDateTo ? departure.aDateTo : null;
        this.params.transportUnitId = departure && departure.flightUnitId ? departure.flightUnitId : null;

        //this.params.dateFrom = ! this.params.dateFrom && departures.length ? departures[0].dateFrom : this.params.dateFrom;
        //this.params.dateTo = ! this.params.dateTo && this.params.dateFrom && departures.length ? departures[0].dateTo : this.params.dateTo;
    },
    getParams() {
        this.params = details.getParams(this.params, this.defaults);
        this.params.guests = this.params.guests && this.params.guests.startsWith('0') ? null : this.params.guests;
        if(this.params.dates) {
            this.splitCalendarDates(this.params.dates);
        }
    },
    splitCalendarDates(dates) {
        if(dates || dates === '') {
            const aDates = dates.split(' - ');
            this.params.dateFrom = aDates[0] ? aDates[0] : null;
            this.params.dateTo = aDates[1] ? aDates[1] : null;
        }
    },
    getCalculation(params) {
        const cParams = params ? params : this.params;
        // cParams.calcPerService = 1;
        const calculation = details.getCalculation(cParams);

        if(calculation) {
            details.unitsSpinner();
            showCalcLoader(this.$root);
            scrollToResults(this.$root, '.tickets');

            //this.$q('.rate-container').addClass('d-none');

            calculation
                .done(response => {
                    this.calculationDone(response, cParams);
                }).fail(error => {
                    console.log(error);
                }).always(() => {
                    hideCalcLoader(this.$root);
                });

            return;

        } else {
            this.setPrices();
        }

        this.$q('.rate-container').removeClass('d-none');
        this.$q('.block-totals-per-service').addClass('d-none');
        this.$q('.unit-error-message').addClass('d-none');
    },
    setPrices() {
        let guests = this.params.guests && !this.params.guests.startsWith('0');

        if(!guests) {
            let prices = JSON.parse(this.$q('[data-prices]').attr('data-prices'));
            prices = prices[this.params.dateFrom + ',' + this.params.dateTo];
            details.unitsSpinner();

            this.$q('.unit').attr('style', 'order: 10000');
            if(prices) {
                setTimeout(() => {
                    Object.entries(prices).forEach(item => {
                        const unitId = item[0];
                        const rate = item[1];

                        const $unit = this.$q('.unit[data-unitid="' + unitId + '"]');

                        $unit.find('.price').text(rate.priceWithDiscount ? rate.priceWithDiscount : rate.price);
                        $unit.find('.price-wrapper').removeClass('d-none');
                        $unit.find('.price-from-label').removeClass('d-none');
                        $unit.find('.total-label').addClass('d-none');

                        let price = rate.priceWithDiscount ? rate.priceWithDiscount : rate.price;
                        price = price.replace('€ ', '');
                        price = price.replace('.', '');
                        let order = parseInt(price);
                        $unit.prop('style', 'order: ' + order);
                    });
                }, 800);
            }
        }
    },
    preparePriceAndBooking(unitId, $unit, unitData, params) {

        const $totalPerServiceBlock = $unit.find('.totals-per-service');
        const totalPerServiceHtml = $totalPerServiceBlock.html();

        let discountTitle;
        $totalPerServiceBlock.html('');

        const unitServices = Object.keys(unitData.services);
        const unitServiceCount = unitServices.length;

        unitServices.forEach(( serviceId) => {

            const dataPerService = unitData.services[serviceId];

            const unitCalc = dataPerService.calc;
            const currencySymbol = unitCalc.currencySymbol;
            const bookingStatus = dataPerService.booking.status;
            const unitCalcItems = unitCalc.items;
            const total = +unitCalc.total.toFixed(2);
            const pricelistid = unitCalc.items[0].pricelistId;

            const $totalPerServiceHtml = $(totalPerServiceHtml);

            let priceOld = details.getCalcTotalOld(unitCalcItems);
            priceOld = +priceOld.toFixed(2);

            let serviceText = '';
            if (dataPerService.customServiceName) {
                serviceText = dataPerService.customServiceName;
            } else {
                packageTab.allowedServices.forEach((allowedService) => {
                    if (dataPerService.serviceId && +allowedService.id === +dataPerService.serviceId ||
                        dataPerService.serviceCode && allowedService.key === dataPerService.serviceCode){
                        serviceText = allowedService.text;
                    }
                });

                if ( ! serviceText)
                {
                    if (dataPerService.serviceName) {
                        serviceText = dataPerService.serviceName;
                    }
                }
            }

            let totalRounded = functions.formatMoney(total, 0, ',', '.');
            $totalPerServiceHtml.attr('style', 'order:' + totalRounded);
            $totalPerServiceHtml.addClass('order-' + totalRounded);
            $totalPerServiceHtml.attr('data-order' , totalRounded);

            const $radio = $totalPerServiceHtml.find('[type="radio"]');

            const radioVal = dataPerService.serviceId || dataPerService.serviceCode;
            $radio.val(radioVal);
            $radio.attr('name', 'service-' + unitId);
            $radio.attr('id', 'service-' + unitId + '-' + radioVal);
            $radio.attr('data-servicekey', dataPerService.serviceCode ? 'serviceCode' : 'serviceId');

            let extra = {};
            if (dataPerService.optionRefId) {
                extra.optionRefId = dataPerService.optionRefId;
            }

            $radio.attr('data-extra', JSON.stringify(extra));

            const $label = unitServiceCount > 1 ? $totalPerServiceHtml.find('label') : $unit.find('.service-label');
            $label.text(serviceText);
            $label.attr('for', 'service-' + unitId + '-' + radioVal);

            const $oldTotal = unitServiceCount > 1 ? $totalPerServiceHtml.find('[data-oldtotalperservice]') : $unit.find('.price-old');
            $oldTotal.text(priceOld.toFixed(0) > total.toFixed(0) ? currencySymbol + ' ' + functions.formatMoney(priceOld, 0, ',', '.') : '');
            $oldTotal.attr('data-oldtotalperservice', priceOld.toFixed(0) > total.toFixed(0) ? priceOld : '');

            const $total = unitServiceCount > 1 ? $totalPerServiceHtml.find('[data-totalperservice]') : $unit.find('.price');
            $total.text(currencySymbol + ' ' + functions.formatMoney(total, 0, ',', '.'));
            $total.attr('data-totalperservice', total);
            $total.attr('data-currency', currencySymbol);
            $total.attr('data-bookingStatus', bookingStatus);
            $total.attr('data-pricelistid', pricelistid);

            $totalPerServiceBlock.append($totalPerServiceHtml[0].outerHTML);
        });

        let minp = null;
        $totalPerServiceBlock.find("[data-order]").each((index, elem) => {
            let p = +$(elem).attr('data-order');
            if (minp === null || p < minp) {
                minp = p;
            }
        });

        $totalPerServiceBlock.find(".order-" + minp).find('[type="radio"]').click();
        $totalPerServiceBlock.find(".order-" + minp).addClass('border-0');
    },
    calculationDone(response, params) {
        if(response.status) {
            const data = response.data || [];

            Object.entries(data).forEach(unit => {
                const unitId = unit[0];
                let unitData = unit[1];
                const $unit = this.$q('.unit[data-unitid=' + unitId + ']');
                if ( ! $unit.length ) return;
                let isBookable = false;
                
                 // normalize response: wrap flat data into services structure
                if(!unitData.services && unitData.calc) {
                    unitData = {
                        services: {
                            0: {
                                calc: unitData.calc,
                                booking: unitData.booking,
                                status: unitData.status,
                                message: unitData.message,
                                condition: unitData.condition || '',
                                isAvailable: unitData.isAvailable,
                                serviceId: null,
                                serviceCode: null
                            }
                        }
                    };
                }

                const unitServiceCount = unitData.services ? Object.values(unitData.services).length : 0;
                $unit.find('.rate-container').length && $unit.find('.rate-container').toggleClass('d-none', unitServiceCount > 1);

                if(unitData.services && details.isBookablePerService(unitData.services)) {
                    isBookable = details.isBookablePerService(unitData.services);
                    const total = details.getMinPricePerService(unitData.services);
                    packageTab.preparePriceAndBooking(unitId, $unit, unitData, params);
                    $unit.prop('style', 'order: ' + parseInt(total));
                } else {
                    $unit.prop('style', 'order: 100000;');
                }
                

                let error = ! isBookable ? details.getCalcErrorMessage(this.params.calcErrorMessages, unitData) : null;

                $unit.find('.unit-error-message').toggleClass('d-none', isBookable);
                $unit.find('.unit-error-message').text(error);

                $unit.find('.price-from-label').toggleClass('d-none', isBookable);
                $unit.find('.total-label').toggleClass('d-none', ! isBookable);
                $unit.find('.price-wrapper').removeClass('d-none');

                $unit.find('.block-totals-per-service').toggleClass('d-none', ! isBookable || unitServiceCount < 2);
                $unit.find('.fast-and-secure-label').toggleClass('d-none', ! isBookable);
                $unit.find('.add-to-cart').toggleClass('d-none', ! isBookable);
                $unit.find('.btn-book-one-unit').toggleClass('d-none', ! isBookable);
                $unit.find('.btn-request').toggleClass('d-none', isBookable);

                if(isBookable && unitData.services) {
                    let hasRequest = false;
                    Object.values(unitData.services).forEach(service => {
                        if(service.booking && service.booking.status === 'Request') {
                            hasRequest = true;
                        }
                    });
                    const btnText = hasRequest ? 'Pošaljite upit' : 'Rezervišite';
                    $unit.find('.btn-book-one-unit').text(btnText);
                }

            });
        }
    }
};
